/* eslint-disable max-lines */
import styled, { css } from "styled-components";
import {
  Blank,
  Flex,
  Description as DescriptionUI,
  FieldValue as FieldValueUI,
  DialogContent as UIDialogContent,
  PaginationSimple,
  Link,
  Chip,
  Icon,
  LinearProgress,
  transition,
} from "@evergis/ui";

import { SmallPreviewControl, SmallPreviewCounter } from "./components/CameraAttribute/styled";
import { SvgContainer } from "./components/FeatureCardSvg/styled";
import { FILTERED_VALUE_OPACITY } from "./constants";
import { LEFT_PANEL_MIN_WIDTH } from "../../components/Layout/constants";
import { BottomState } from "../../ducks/layout/layout";

export const CardsContainer = styled.div`
  z-index: 99;
  overflow: initial;
  right: 0;
  top: 0;
  position: absolute;
  display: flex;
  flex-direction: column;

  ${Blank} {
    background-color: ${({ theme: { palette } }) => palette.panelBackground};
    backdrop-filter: blur(10px);
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  padding: 1rem;

  ${Flex} {
    &:first-child {
      flex-grow: 1;
      margin-right: 2rem;
    }
  }
`;

export const HeaderContainer = styled(Flex)`
  flex-wrap: nowrap;
  max-width: calc(100% - 4rem);
  width: calc(100% - 4rem);
`;

export const HeaderTitle = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  width: 100%;
  color: ${({ theme: { palette } }) => palette.textPrimary};
`;

export const LayerDescription = styled(DescriptionUI)`
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const RowHeaderMixin = css`
  &&& {
    min-height: auto;

    ${HeaderTitle}, ${LayerDescription} {
      text-align: left;
    }
  }

  ${HeaderContainer} {
    flex-direction: row;
  }

  ${HeaderTitle} {
    max-width: calc(100% - 3.8rem);
  }
`;

export const Header = styled(Flex)<{ $isRow?: boolean }>`
  z-index: 1;
  position: sticky;
  top: 0;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.medium};
  padding: 0.75rem;

  ${HeaderContainer} {
    flex-direction: column;
  }

  ${({ $isRow }) => $isRow && RowHeaderMixin};
`;

export const Controls = styled(HeaderContainer)`
  position: sticky;
  top: 4.125rem;
  padding: 0.5rem 1rem;
  max-width: calc(100% - 2rem);
  width: calc(100% - 2rem);
  height: 2rem;
  flex-shrink: 0;
  background-color: ${({ theme: { palette } }) => palette.elementDark};

  &&& {
    flex-direction: row;
  }
`;

export const FeatureCardButtonsContainer = styled(Flex)``;

export const StyleIconImage = styled.div`
  img {
    max-width: 2.5rem;
    max-height: 2.5rem;
  }
`;

export const FeatureCardChipsContainer = styled(Flex)``;

export const TitleContainer = styled(Flex)`
  flex-direction: column;
  width: calc(100% - 3.5rem);
`;

const HeaderFontColorMixin = css<{ $fontColor?: string }>`
  ${HeaderTitle}, ${LayerDescription}, ${FeatureCardButtonsContainer} span[kind]:after {
    color: ${({ $fontColor }) => $fontColor};
  }
`;

const HeaderWrapperMixin = css<{ $fontColor?: string }>`
  padding: 0.5rem 0.5rem 0;

  ${Header} {
    min-height: 5.25rem;
  }

  ${HeaderContainer} {
    max-width: 100%;
    width: 100%;
  }

  ${Controls} {
    max-width: calc(100% - 2rem);
    width: calc(100% - 2rem);
    margin-top: -0.5rem;
    padding-top: 1rem;
    border-radius: ${({ theme: { borderRadius } }) => borderRadius.medium};
  }

  ${FeatureCardButtonsContainer} {
    position: absolute;
    top: 0;
    right: 0;
  }

  ${({ $fontColor }) => !!$fontColor && HeaderFontColorMixin};
`;

export const GradientHeaderWrapper = styled.div<{ $fontColor?: string; $bgColor?: string }>`
  ${Header} {
    background: ${({ $bgColor }) =>
      $bgColor || "radial-gradient(129.21% 133.22% at 51.94% 0%, #e8fffe 9.48%, #5fcaff 100%)"};
  }

  ${HeaderContainer} {
    align-items: center;
  }

  ${TitleContainer} {
    margin-left: 0;
    text-align: center;
  }

  ${HeaderTitle} {
    text-align: center;
  }

  ${LayerDescription} {
    text-align: center;
  }

  ${HeaderWrapperMixin};
`;

export const HeaderIcon = styled(Flex)`
  position: absolute;
  top: 0;
  right: 0;
  justify-content: center;
  align-items: center;
  min-width: 7.5rem;
  height: 100%;

  span[kind]:after {
    font-size: 7.5rem;
  }

  span[kind]:after,
  path,
  line,
  circle {
    fill: rgba(255, 255, 255, 0.36);
  }

  && > * {
    display: flex;
    align-items: center;
    height: 100%;
  }
`;

const BigIconHeaderMixin = css`
  ${HeaderIcon} {
    min-width: 14rem;
    right: -3rem;

    span[kind]:after {
      font-size: 14rem;
    }
  }
`;

export const IconHeaderWrapper = styled.div<{ $fontColor?: string; $bgColor?: string; $bigIcon?: boolean }>`
  ${Header} {
    background: ${({ $bgColor }) => $bgColor || "linear-gradient(96.55deg, #FFFCD3 0%, #B4DC47 100%)"};
  }

  ${HeaderWrapperMixin};

  ${({ $bigIcon }) => $bigIcon && BigIconHeaderMixin};
`;

export const SlideshowHeaderWrapper = styled.div<{
  big?: boolean;
  withPadding?: boolean;
}>`
  padding: ${({ withPadding }) => (withPadding ? "0.5rem 0.5rem 0" : 0)};

  ${Header} {
    height: ${({ big }) => (big ? "15.5rem" : "auto")};
    border-radius: ${({ theme: { borderRadius } }) => borderRadius.medium};

    ${SmallPreviewCounter} {
      bottom: ${({ withPadding }) => (withPadding ? 0.625 : 1.125)}rem;
    }

    :before,
    :after {
      content: "";
      z-index: 2;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
    }

    :before {
      height: 100%;
      background: rgba(32, 46, 53, 0.25);
    }

    :after {
      height: 4.5rem;
      background: linear-gradient(180deg, #000000 0%, rgba(0, 0, 0, 0) 100%);
    }

    :hover {
      ${SmallPreviewControl}, ${SmallPreviewCounter} {
        opacity: 1;
      }
    }
  }

  ${Controls} {
    margin-top: ${({ withPadding }) => (withPadding ? -0.5 : 0)}rem;
    padding-top: ${({ withPadding }) => (withPadding ? 1 : 0.5)}rem;
    border-bottom: ${({ withPadding, theme: { palette } }) => (withPadding ? 0 : `1px ${palette.element} solid`)};
    border-radius: ${({ withPadding, theme: { borderRadius } }) =>
      withPadding
        ? `0 0 ${borderRadius.medium} ${borderRadius.medium}`
        : `${borderRadius.medium} ${borderRadius.medium} 0 0`};
    background: ${({ withPadding, theme: { palette } }) => (withPadding ? palette.element : palette.background)};
  }
`;

export const HeaderFrontView = styled(Flex)`
  z-index: 10;
  position: relative;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font: ${({ theme: { fonts } }) => fonts.subtitle};
`;

export const HeaderSlideshow = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  img {
    width: 100%;
    height: 100%;
    min-height: inherit;
    object-position: center;
    object-fit: cover;
  }
`;

export const Card = styled(Blank)<{ bottomState?: BottomState; bottomHeight?: number }>`
  position: relative;
  right: 0.75rem;
  top: 2.75rem;
  width: ${`${LEFT_PANEL_MIN_WIDTH}px`};
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: initial;
  box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.15);
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.medium};
  max-height: calc(
    100vh - 2.25rem - 1rem - ${({ bottomState, bottomHeight }) => (bottomState === "opened" ? bottomHeight : 0)}px
  );

  ${LinearProgress} {
    position: absolute;
    top: 0;
    left: 0;
    min-height: 0.25rem;
    max-height: 0.25rem;
    border-radius: ${({ theme: { borderRadius } }) => `${borderRadius.xxLarge} ${borderRadius.xxLarge} 0 0`};
    overflow: hidden;
  }

  @media screen and (max-width: 100.75rem) {
    max-height: calc(100vh - 2.25rem - 4.5rem - ${({ bottomState }) => (bottomState === "opened" ? 350 : 0)}px);
  }
`;

export const BackCard = styled(Card)`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 3.5rem;
  justify-content: space-between;
  position: absolute;
  right: 0.75rem;
  top: 0.75rem;
  box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.15);

  + ${Card} {
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.15);
  }
`;

export const ContainerWrapper = styled.div`
  transition: background-color ${transition.toggle};
`;

export const FeatureCardChip = styled(Chip)<{
  $bgColor?: string;
  $fontColor?: string;
  $fontSize?: string;
  $radius?: string;
  $isDefault?: boolean;
}>`
  margin: 0 0.25rem 0.25rem 0;
  background: ${({ $isDefault, $bgColor, theme: { palette } }) =>
    $isDefault ? palette.element : $bgColor || palette.primary};
  border-radius: ${({ $radius, theme: { borderRadius } }) => $radius || borderRadius.medium};
  font-size: ${({ $fontSize }) => $fontSize || "0.875rem"};
  color: ${({ $isDefault, $fontColor, theme: { palette } }) =>
    $isDefault ? palette.textPrimary : $fontColor || "#fff"};

  > * {
    font-size: ${({ $fontSize }) => $fontSize || "0.875rem"};
    color: ${({ $isDefault, $fontColor, theme: { palette } }) =>
      $isDefault ? palette.textPrimary : $fontColor || "#fff"};
  }

  span[kind]:after {
    color: ${({ $isDefault, $fontColor, theme: { palette } }) => ($isDefault ? palette.icon : $fontColor || "#fff")};
  }
`;

export const HiddenFilters = styled(Flex)`
  margin-top: -1.25rem;

  ${FeatureCardChip} {
    height: 1.5rem;
    margin: 0 0.25rem 0.25rem 0;
    padding: 0 0 0 0.5rem;
  }
`;

export const Main = styled(Flex)<{ padding?: string }>`
  padding: ${({ padding = "1rem" }) => padding};
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable;

  > * > ${ContainerWrapper} {
    > * {
      width: calc(100% - 0.5rem);
    }

    :not(:last-child) > * {
      margin-bottom: 2rem;
    }
  }

  ${HiddenFilters} {
    margin-top: 0.75rem;
  }
`;

export const FieldValue = styled(FieldValueUI)`
  text-align: start;
  unicode-bidi: plaintext;

  > span:last-of-type {
    word-wrap: break-word;

    & > span {
      text-align: unset !important;
      unicode-bidi: unset !important;
    }
  }
`;

export const DialogContent = styled(UIDialogContent)`
  align-items: center;
  padding-top: 2.5rem;
  padding-bottom: 1rem;
`;

export const UndosContainer = styled.span`
  display: flex;
`;

export const EditButtonsContainer = styled.div`
  display: flex;
  flex-grow: 1;
`;

export const EditText = styled.div`
  display: flex;
  flex-grow: 1;
`;

export const FeaturesPagination = styled(PaginationSimple)`
  padding-left: 0.6rem;
  padding-bottom: 1rem;
  user-select: none;
  height: 2.25rem;
`;

export const StatCount = styled(DescriptionUI)`
  padding-left: 1rem;
  padding-bottom: 1rem;
  user-select: none;
  height: 2.25rem;
`;

export const FeatureNumber = styled(Link)`
  font: ${({ theme: { fonts } }) => fonts.description};
  padding: 0 0.4rem;
`;

export const FeatureTools = styled.div`
  display: flex;
  align-items: center;
  height: 2.25rem;
  gap: 0.5rem;
  padding-right: 0.6rem;
`;

export const FeatureToolsDividerContainer = styled.div`
  height: 1rem;
`;

export const StyleIconBackground = styled(Flex)`
  align-items: center;
  justify-content: center;
  position: relative;
  box-sizing: border-box;
  width: 2.5rem;
  min-height: 2.5rem;
  padding: 0.2rem;

  &:before {
    content: "";
    opacity: 0.3;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    position: absolute;
    z-index: -1;
  }
`;

export const ValueLink = styled.a`
  color: ${({ theme: { link } }) => link.color.primary};
  text-decoration: none;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const AttributeLabel = styled(DescriptionUI)<{ isEdit?: boolean; forCheckbox?: boolean }>`
  margin-top: 0 !important;
  margin-bottom: ${({ forCheckbox }) => (forCheckbox ? "0.75rem" : "0.25rem")} !important;
  padding-left: ${({ isEdit }) => (isEdit ? "0.5rem" : "0")};
`;

export const GeometryAttributeValue = styled.div`
  color: ${({ theme: { link } }) => link.color.primary};
  cursor: pointer;
`;

const ColorIconMixin = css<{ $fontColor?: string }>`
  :after {
    color: ${({ $fontColor }) => $fontColor} !important;
  }
`;

const SizeIconMixin = css<{ $fontSize?: number }>`
  :after {
    font-size: ${({ $fontSize }) => $fontSize}px !important;
  }
`;

export const FeatureCardIcon = styled(Icon)<{ $fontColor?: string; $fontSize?: number }>`
  width: auto;
  height: auto;
  margin-bottom: 0.5rem;
  ${({ $fontColor }) => !!$fontColor && ColorIconMixin};
  ${({ $fontSize }) => !!$fontSize && SizeIconMixin};
`;

export const TabAnchor = styled.div`
  position: absolute;
  top: -1.5rem;
  right: 0;
`;

export const TabValue = styled(Flex)`
  flex-wrap: nowrap;
`;

const noBgMixin = css<{ active?: boolean; bgColor?: string }>`
  background-color: transparent;
  border-radius: 0;
  border-bottom: 0.125rem solid
    ${({ active, bgColor, theme: { palette } }) => (active ? bgColor || palette.primary : palette.element)};

  ${TabValue},
  span[kind] {
    color: ${({ active, bgColor, theme: { palette } }) =>
      active ? bgColor || palette.primary : palette.textSecondary};
  }

  &&& svg {
    path,
    line,
    circle {
      fill: ${({ active, bgColor, theme: { palette } }) =>
        active ? bgColor || palette.primary : palette.textSecondary};
    }
  }

  :not(:last-child) {
    margin-right: 0;
  }
`;

export const TabContainer = styled.a<{
  active?: boolean;
  radius?: number;
  bgColor?: string;
  noBg?: boolean;
  hasIcon?: boolean;
  column?: boolean;
  onlyIcon?: boolean;
}>`
  display: flex;
  flex-direction: ${({ column }) => (column ? "column" : "row")};
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  padding: ${({ onlyIcon }) => (onlyIcon ? 1 : 0.5)}rem 1rem;
  background-color: ${({ active, bgColor, theme: { palette } }) =>
    active ? bgColor || palette.primary : palette.element};
  border-radius: ${({ column, radius, theme: { borderRadius } }) =>
    radius ? `${radius}px` : column ? borderRadius.medium : borderRadius.xLarge};
  text-decoration: none;

  :not(:last-child) {
    margin-right: 0.5rem;
  }

  ${TabValue} {
    margin-top: ${({ column }) => (column ? 0.5 : 0)}rem;
    margin-left: ${({ column, hasIcon }) => (column || !hasIcon ? 0 : 0.5)}rem;
    font-size: ${({ column }) => (column ? 0.75 : 0.875)}rem;
    white-space: nowrap;
  }

  ${TabValue},
  span[kind] {
    color: ${({ active, theme: { palette } }) => (active ? palette.textContrast : palette.textSecondary)};
  }

  ${SvgContainer} {
    height: 1rem;
  }

  svg,
  img {
    max-width: 1rem;
    max-height: 1rem;
  }

  ${({ noBg }) => noBg && noBgMixin};
`;

export const ChartTooltipTable = styled.table`
  td {
    padding: 0;
  }

  tr:not(:last-child) td {
    padding-bottom: 0.375rem;
  }
`;

export const ChartTooltip = styled(Flex)`
  flex-direction: row;
  align-items: center;
  flex-wrap: nowrap;

  :not(:last-child) {
    margin-bottom: 0.25rem;
  }
`;

export const ChartTooltipColor = styled.div<{ $color: string }>`
  width: 0.625rem;
  height: 0.625rem;
  background-color: ${({ $color }) => $color};
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.smallest};
  margin-right: 0.25rem;
`;

export const ChartTooltipName = styled.div`
  margin-right: 1rem;
  opacity: 0.65;
`;

export const FeatureAttributesContainer = styled(Flex)`
  width: 100%;

  &&& {
    > * {
      width: calc(100% - 2rem);

      :not(:last-child) {
        margin-bottom: 1rem;
      }
    }
  }
`;

export const InnerContainerWrapper = styled.div<{
  hasAnyFilter?: boolean;
  isFiltered?: boolean;
  column?: boolean;
  onClick?: VoidFunction;
}>`
  cursor: ${({ onClick }) => (onClick ? "pointer" : "default")};
  width: ${({ column }) => (column ? "100%" : "auto")};
  opacity: ${({ hasAnyFilter, isFiltered }) => (isFiltered ? 1 : hasAnyFilter ? FILTERED_VALUE_OPACITY / 100 : 1)};
`;

export const DataSourceErrorContainer = styled(Flex)`
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  flex-grow: 1;
  padding: 1rem;
  border: 1px ${({ theme: { palette } }) => palette.element} solid;
  border-radius: 10px;
  font-size: 0.875rem;
  color: ${({ theme: { palette } }) => palette.textDisabled};

  span[kind] {
    margin-right: 1rem;

    :after {
      color: ${({ theme: { palette } }) => palette.iconDisabled};
    }
  }
`;

export const PresentationButton = styled(Flex)`
  width: 2rem;
  height: 1rem;
  margin: 0 -0.5rem 0 0.25rem;
  padding-left: 0.5rem;
  border-left: 1px ${({ theme: { palette } }) => palette.elementDeep} solid;

  > button {
    margin-top: -0.5rem;

    span[kind="play"] {
      width: 0.75rem;
      height: 0.75rem;
      border-radius: 50%;
      border: 2px ${({ theme: { palette } }) => palette.icon} solid;

      :after {
        top: 2px;
        left: 2px;
        font-size: 0.55rem;
      }
    }
  }
`;

export const PagesWrapper = styled(Flex)`
  flex-grow: 1;
  justify-content: space-between;
  align-items: center;
`;

export const PageName = styled.div`
  font-size: 0.75rem;
  color: ${({ theme: { palette } }) => palette.textPrimary};
`;

const getMaskColor = (isDark?: boolean) => (isDark ? "0, 0, 0" : "255, 255, 255");

export const PresentationHeaderImage = styled.div<{ image?: string; isDark?: boolean }>`
  &&& {
    z-index: 1;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 9.25rem;
    background: url(${({ image }) => image}) 50% 0 no-repeat;
    background-size: cover;
    border-top-left-radius: ${({ theme: { borderRadius } }) => borderRadius.medium};
    border-top-right-radius: ${({ theme: { borderRadius } }) => borderRadius.medium};

    ${({ isDark }) => css`
      -webkit-mask-image: linear-gradient(
        to bottom,
        rgba(${getMaskColor(isDark)}, 1),
        rgba(${getMaskColor(isDark)}, 0)
      );
      mask-image: linear-gradient(to bottom, rgba(${getMaskColor(isDark)}, 1), rgba(${getMaskColor(isDark)}, 0));
    `}
  }
`;

export const PaginationWrapper = styled(Flex)<{ image?: string; isDark?: boolean }>`
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-top: 0.5rem;
  background-color: ${({ theme: { palette } }) => palette.background};
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.medium};

  ${({ image }) =>
    image
      ? css`
          position: relative;
          flex-direction: column;
          align-items: flex-start;
          justify-content: flex-start;
          width: calc(100% + 3rem);
          min-height: 7.25rem;
          margin-top: -0.5rem;

          ${PagesWrapper} {
            z-index: 2;
            position: relative;
            width: calc(100% - 2rem);
            margin: 1.5rem 1rem 0;

            ${PageName} {
              font-size: 1.25rem;

              > span {
                background: none;
              }
            }
          }

          &&& ${PresentationHeaderImage} {
            height: 7.75rem;
            background-position: 50% 50%;
            border-radius: 0;
          }

          ${PresentationButton} {
            z-index: 2;
            position: relative;
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: calc(100% - 1.75rem);
            height: 3.5rem;
            border: none;

            > button {
              margin-top: 0;
            }
          }
        `
      : css`
          padding-left: 1.5rem;
          padding-right: 1.5rem;
          padding-bottom: 0.5rem;
        `};
`;

export const PageNavigator = styled(Flex)`
  align-items: center;
  font-size: 0.75rem;
  color: ${({ theme: { palette } }) => palette.textSecondary};
`;

export const CurrentPage = styled.div`
  cursor: pointer;
  margin-top: 0.125rem;
`;
